import {gql} from '@apollo/client';
import Backbone from 'backbone';
import {DocumentNode} from 'graphql';

import * as statusbar from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FileStateAttributes} from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import ApolloFolderItemBase from 'folder-items/ApolloFolderItemBase';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import QueryWrapper from 'graphql-client/QueryWrapper';
import MetadataTabState from 'metadata/MetadataTabState';
import preferences from 'Preferences';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import RelevantItemsTabState from 'relevant-items/RelevantItemsTabState';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ProcessFileQuery} from './__generated/ProcessFileQuery';
import ProcessDetailsTabState from './ProcessDetailsTabState';
import ProcessFlowchartTabState from './ProcessFlowchartTabState';

type ProcessFileGraphQL = ExtractByTypename<ProcessFileQuery['process'], 'ProcessNode'>;

export const PROCESS_FILE_QUERY: DocumentNode = gql`
  query ProcessFileQuery($fileId: StrictString!) @engComponent(name: WORKFLOWS) {
    process(id: $fileId) @audit(msg: "Opened process {name}") {
      ... on ProcessNode {
        name
        humanId
        rootProcess {
          ... on ProcessNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        processTaskSchema {
          id
          name
          executionType
        }
        ...BaseFolderItemFileGraphQL
      }
      ... on InaccessibleInfoNode {
        ...BaseFolderItemFileGraphQL
      }
    }
  }
  ${getFragmentWrapper('BaseFolderItemFile').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export class Process extends ApolloFolderItemBase<ProcessFileQuery, ProcessFileGraphQL> {
  protected getQueryWrapper(): QueryWrapper<ProcessFileQuery, ProcessFileGraphQL> {
    return {query: PROCESS_FILE_QUERY};
  }

  protected queryVariables(): {fileId: string} {
    return {fileId: this.id};
  }
  protected getGraphQLFolderItemFromQueryData(
    data: ProcessFileQuery
  ): ProcessFileGraphQL | InaccessibleInfoGraphQL {
    return data.process;
  }

  protected getUpdatedQueryData(attrs: Partial<ProcessFileGraphQL>): ProcessFileQuery {
    return {
      process: {
        ...this.folderItem(),
        ...attrs,
      },
    };
  }

  projectFolderId(): string {
    return this.folderItem().folder.id;
  }

  editURL(): string {
    return this.folderItem().editUrl;
  }

  humanId(): string {
    return this.folderItem().humanId;
  }

  rootProcessId(): string | null {
    const rootProcess = this.folderItem().rootProcess;
    return rootProcess ? (isInaccessible(rootProcess) ? null : rootProcess.id) : null;
  }

  executionType(): ProcessExecutionType {
    return this.folderItem().processTaskSchema.executionType;
  }

  taskSchemaName(): string {
    return this.folderItem().processTaskSchema.name;
  }

  isNonInteractiveProcess(): boolean {
    return !getProcessPlatformFeatureConfig(
      ProcessPlatformFeatureType.CAN_EDIT_TASK_GROUPS,
      this.executionType(),
      this.taskSchemaName()
    );
  }
}

interface ProcessFileState extends FolderItemState {
  model(): Process;
}

class ProcessFileState extends FolderItemState {
  static Model: typeof Process;

  static initClass(): void {
    this.Model = Process;
    this.fileType = ModelTypeConstants.PROCESS;
    this.prototype.hasNote = false;
  }

  defaults(): FileStateAttributes {
    return {
      ...super.defaults(),
      toolbar: new Backbone.Model(),
      statusbar: new statusbar.StatusBarState(),
    };
  }

  hasMetadata(): boolean {
    return true;
  }

  shouldDisplayFlowchartTab(): boolean {
    return (
      this.model().executionType() === ProcessExecutionType.FLOWCHART || this.model().rootProcessId() != null
    );
  }

  setupPrimaryBufferTab(): void {
    const processDetailsTab = new ProcessDetailsTabState({fileId: this.id});
    this.buffers().at(0).tabs().add(processDetailsTab);
    processDetailsTab.set({active: true});

    if (this.metadataTab != null) {
      this.buffers().at(0).add(this.metadataTab);
    }

    if (this.relevantItemsTab != null) {
      this.buffers().at(0).add(this.relevantItemsTab);
    }
  }

  setupSecondaryBufferTab(): void {
    const tabs = [];

    if (this.shouldDisplayFlowchartTab()) {
      tabs.push(new ProcessFlowchartTabState({fileId: this.id, processFileState: this}));
    }

    if (tabs.length > 0) {
      tabs.forEach((tab) => {
        this.buffers().at(1).tabs().add(tab);
      });
      const splitPref = preferences.get(this.getSplitWorkspacePreferenceKey());
      this.setIsWorkspaceSplit(splitPref != null ? splitPref : true);
    } else {
      this.set('statusbar', new statusbar.SingleBufferStatusBarState());
    }
  }

  initialize(attributes: {}, options: unknown): void {
    super.initialize(attributes, options);

    this.setupPrimaryBufferTab();
    this.setupSecondaryBufferTab();
  }

  updateProcess(process: Process): void {
    if (this.model()) {
      this.stopListening(this.model());
      this.cleanUp();
    }
    this.buffers().forEach((buffer) => {
      buffer.tabs().forEach((tab) => {
        if (tab instanceof MetadataTabState || tab instanceof RelevantItemsTabState) {
          // The metadata and relevant items tabs listen to and update based on changes to the Process FolderItemModel
          tab.set('model', process);
        } else {
          tab.set('fileId', process.id);
        }
      });
    });
    this.set(this.fileType(), process);
    this.set('name', this.model().name());
    this.syncTitleAndURL();
  }

  async setId(id: string): Promise<ProcessFileState> {
    const model = new Process({id});
    model.set({fileState: this});
    this.set('id', id);

    await model.fetch({data: {view: true}});
    /**
     * this.get('id') may have changed after model.fetch if there was another request to setId that came
     * before this one resolved. In this case, the model is out of date and we should not use it.
     * e.g: User selects a Worksheet from dropdown, then immediately selects another while the previous one is still rendering.
     */
    if (this.get('id') === id) {
      this.updateProcess(model);
    }
    return this;
  }
}

ProcessFileState.initClass();

export default ProcessFileState;
